import cx from 'classnames'
import { ReactNode, useCallback, useMemo } from 'react'
import { isOpenFinRuntime } from '../../../containers/OpenFin/helpers'
import {
  formatAggressorLLPrice,
  formatToWords
} from '../../../helpers/formatting'
import {
  QuoteRequest,
  QuoteRequestWithBestResponse
} from '../../../store/rfq/types'
import { SecurityStaticData } from '../../../store/securities/reducer'
import { getSecurityStaticDataById } from '../../../store/securities/selectors'
import { useAppSelector } from '../../../store/types'
import { RfqWindowInfo, SecurityModalInfo } from '../../../store/windows/types'
import {
  formatSpread,
  getOppositeDirection,
  orderTypeToDisplayName
} from '../../DepthOfMarket/helpers'
import QuoteCard from '../../RFQ/QuoteCard'
import {
  getDisplayStatus,
  quoteStatusIsInactive,
  useQuoteRequest
} from '../../RFQ/utils'
import { useSecurityModalInfo } from '../AggressorModal/helpers'
import DisplayValue from '../DisplayValue/DisplayValue'
import styles from '../modal.module.scss'
import footerStyles from '../ModalFooter/modalFooter.module.scss'
import HeaderModal from '../ModalHeader/ModalHeader'
import ModalNotification from '../ModalNotification/ModalNotification'
import ModalSecurityInformationWrapper from '../ModalSecurityInformationWrapper'
import OrderStatusMessage from './OrderStatusMessage'
import RfqModalPanel from './Panels/RfqModalPanel'
import RFQModalFooter from './RFQModalFooter'

type ModalProps = {
  rfq: QuoteRequest
  securityModalInfo: SecurityModalInfo
  security: SecurityStaticData
  handleMinimize: (index: number) => void
  isMinimized: boolean
}

const addDisplayName = (component: ReactNode) => {
  const Result = () => component
  Result.displayName = 'OrderStatusMessage'
  return Result
}

const NullComponent = () => null
NullComponent.displayName = 'NullComponent'

const RFQModal = ({
  rfq,
  handleMinimize,
  securityModalInfo,
  security,
  isMinimized
}: ModalProps) => {
  const isOpenFin = isOpenFinRuntime()
  const bodyStyle = isMinimized ? styles.collapsedModal : styles.expandableModal
  const {
    exposureDurationValues,
    acceptQuote,
    rejectQuote,
    cancelRequest,
    handleClose,
    formatSize
  } = useQuoteRequest(rfq, security)

  const goodFor = useMemo(() => {
    return exposureDurationValues.find((v) => v.value === rfq.exposureDuration)
      ?.label
  }, [rfq.exposureDuration])
  const onRequestCancel = useCallback(() => {
    cancelRequest()
  }, [cancelRequest])

  const quote = rfq.bestResponse
  // footer only shows button if we give it a function to use
  const handleCancel =
    rfq.isLive && !rfq.isExpired ? onRequestCancel : undefined

  const borderColor = `var(--rfqColor)`
  const isTreasury = security.product === 'PrinUSGovtOutright'
  const isUpdated = quote?.status === 'Update'

  const StatusMessage = useMemo(() => {
    if (rfq.isExpired && !rfq.bestResponse) {
      return addDisplayName(
        <OrderStatusMessage
          title="RFQ Timed out"
          className={footerStyles.bottomContentTimedOut}
          content="Due by time has passed"
        />
      )
    }

    switch (getDisplayStatus(quote)) {
      case 'Traded': {
        // for TS
        if (!quote) {
          return NullComponent
        }
        const quotePrice = formatAggressorLLPrice(
          quote.markedPrice,
          security.currency,
          security.product === 'PrinUSGovtOutright'
        )
        const quoteSpread = formatSpread(quote.markedSpread)
        const displayPrice = rfq.isSpread
          ? `${quoteSpread} (${quotePrice})`
          : quotePrice
        const directionPastTense = rfq.type === 'buy' ? 'bought' : 'sold'
        const successMsg = `You ${directionPastTense} ${formatSize(
          rfq.size
        )} @ ${displayPrice}`

        return addDisplayName(
          <OrderStatusMessage
            title={directionPastTense.toUpperCase()}
            content={successMsg}
            className={footerStyles.bottomContentSuccess}
          />
        )
      }
      case 'Rejected': {
        const rejectMsg = 'Aggress rejected by counterparty'
        return addDisplayName(
          <OrderStatusMessage
            title="Rejected"
            className={footerStyles.bottomContentRejected}
            content={rejectMsg}
          />
        )
      }
      case 'Pass': {
        const rejectMsg = `You rejected this ${orderTypeToDisplayName(
          getOppositeDirection(rfq.type)
        )}`
        return addDisplayName(
          <OrderStatusMessage
            title="Pass"
            className={footerStyles.bottomContentRejected}
            content={rejectMsg}
          />
        )
      }
      case 'Cancel': {
        return addDisplayName(
          <OrderStatusMessage
            title="Cancelled"
            className={footerStyles.bottomContentCancelAttempt}
            content="Quote cancelled by counterparty"
          />
        )
      }
      case 'QRExpire': {
        return addDisplayName(
          <OrderStatusMessage
            title="Expired"
            className={footerStyles.bottomContentTimedOut}
            content="Request for Quote Expired"
          />
        )
      }
    }
    if (rfq.status && !['InProgress', 'HitLift'].includes(rfq.status)) {
      const displayStatus = formatToWords(rfq.status)
      return addDisplayName(
        <OrderStatusMessage
          title={displayStatus}
          className={footerStyles.bottomContentTimedOut}
          content={`Quote Request ${displayStatus}`}
        />
      )
    }
    return NullComponent
  }, [
    rfq.type,
    rfq.size,
    rfq.isExpired,
    rfq.isSpread,
    rfq.status,
    rfq.bestResponse,
    quote,
    formatSize,
    security.currency
  ])

  const showQuoteCard = !!quote && rfq.status !== 'Rejected'

  return (
    <div
      className={styles.modalWrapper}
      key={`${rfq.requestId}-${security.id}`}
      data-testid={`rfq-modal-${rfq.type}-${security.id}`}
    >
      <section className={styles.modalMain} style={{ borderColor }}>
        <HeaderModal
          type={rfq.type}
          prefix="RFQ:"
          security={security}
          showSecurityInfo={true}
          issue={securityModalInfo.issue}
          showMinimize={!isOpenFin}
          isActiveWindow={!isMinimized}
          index={-1}
          handleMinimize={handleMinimize}
        />
        {isUpdated && (
          <ModalNotification
            colorStyle="alert"
            messageNotification="Price has changed"
          />
        )}
        <div className={cx(styles.contentExpandable, bodyStyle)}>
          <div className={styles.content}>
            <RfqModalPanel title="RFQ Criteria" testid="rfq-info">
              <DisplayValue
                label="RFQ Amt"
                value={formatSize(rfq.size)}
                className={styles.rfqEmphasis}
              />
              <div>
                {rfq.isSpread && (
                  <DisplayValue label="Hedge" value="Outright" small />
                )}
              </div>
              {(!quote || !quoteStatusIsInactive(quote)) && (
                <>
                  <DisplayValue
                    label="Due By"
                    value={rfq.isASAP ? 'ASAP' : 'Due Later'}
                    small
                  />
                  {goodFor ? (
                    <DisplayValue label="Good For" value={goodFor} small />
                  ) : (
                    <div></div>
                  )}
                </>
              )}
              {rfq.discloseMyName && <footer>Disclose my name.</footer>}
            </RfqModalPanel>
            <RfqModalPanel title="Order Book Ref">
              <DisplayValue
                small
                label="Quoted Amt"
                value={securityModalInfo.maximumSize}
              />
              {rfq.isSpread && (
                <>
                  <div>{/* to push px/spread to next line*/}</div>
                  <DisplayValue
                    small
                    label="Quoted Spread"
                    value={securityModalInfo.spread}
                  />
                </>
              )}
              <DisplayValue
                small
                label="Quoted Price"
                value={formatAggressorLLPrice(
                  securityModalInfo.price,
                  security.currency,
                  isTreasury
                )}
              />
              <DisplayValue
                small
                label="Bench Price"
                value={securityModalInfo.benchmarkPrice ?? ''}
              />
              <DisplayValue
                small
                label="YTW"
                value={securityModalInfo.yieldToWorst}
              />
            </RfqModalPanel>
          </div>
          <div
            className={styles.quote}
            data-testid={`rfq-modal-${rfq.type}-${security.id}-quote-card`}
          >
            {showQuoteCard ? (
              <>
                <QuoteCard
                  rfq={rfq as QuoteRequestWithBestResponse}
                  currency={security.currency}
                  isTreasury={security.product === 'PrinUSGovtOutright'}
                  accept={acceptQuote}
                  reject={rejectQuote}
                  className={styles.rfqModalQuoteCard}
                  testIdPrefix="rfqModal"
                />
              </>
            ) : rfq.isExpired || !!StatusMessage() ? (
              <></>
            ) : (
              <div className={styles.awaitingResponse}>
                Awaiting response from counterparty...
              </div>
            )}
          </div>
          <StatusMessage />
          <ModalSecurityInformationWrapper
            securityModalInfo={securityModalInfo}
            security={security}
          />
          <RFQModalFooter
            handleCancel={handleCancel}
            handleClose={handleClose}
          />
        </div>
      </section>
    </div>
  )
}

interface WrapperProps {
  rfq: QuoteRequest
  windowInfo: RfqWindowInfo
  handleMinimize: (index: number) => void
  isMinimized: boolean
}

const RFQModalWrapper = ({
  rfq,
  windowInfo,
  handleMinimize,
  isMinimized
}: WrapperProps) => {
  const securityModalInfo = useSecurityModalInfo(
    windowInfo.orderId,
    windowInfo.size
  )
  const security = useAppSelector(getSecurityStaticDataById)(
    securityModalInfo?.securityId ?? -1
  )
  if (!(securityModalInfo && security)) return null
  return (
    <RFQModal
      key={rfq.requestId}
      rfq={rfq}
      security={security}
      securityModalInfo={securityModalInfo}
      handleMinimize={handleMinimize}
      isMinimized={isMinimized}
    />
  )
}

export default RFQModalWrapper
